import React, { Suspense, lazy, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "./context/AuthContext";
import useImportAnonymousShortlist from "./hooks/useImportAnonymousShortlist";

/**
 * Tiny effect-only component, mounted inside <AuthProvider> so we can pick
 * up the token from localStorage right after auth completes. It calls the
 * hook which fires once per session if there's an anonymous shortlist to
 * migrate from an agent's embed widget.
 */
const ShortlistImporter = () => {
  const token = typeof window !== "undefined" ? window.localStorage.getItem("token") : null;
  useImportAnonymousShortlist(token);
  return null;
};
import { CompareProvider } from "./context/CompareContext";
import Navbar from "./components/Navbar";
import RouteErrorBoundary from "./components/RouteErrorBoundary";
import Footer from "./components/Footer";
import { Toaster } from "./components/ui/toaster";
import CompareTray from "./components/CompareTray";
import CookieConsent from "./components/CookieConsent";
import AnalyticsBeacon from "./components/AnalyticsBeacon";
import MaintenanceBanner from "./components/MaintenanceBanner";
import DemoBanner from "./components/DemoBanner";
import InstallPrompt from "./components/InstallPrompt";
import ScrollToTop from "./components/ScrollToTop";
import { initAnalytics } from "./utils/analytics";

/* High-priority routes: keep eagerly imported so they hydrate instantly */
import HomePageProfessional from "./pages/HomePageProfessional";
import ListingsPage from "./pages/ListingsPage";
import PropertyDetailPage from "./pages/PropertyDetailPage";

/* Everything below here is split into its own JS chunk and only loaded when
   the user visits that route. Initial homepage bundle drops by 50-70%. */
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const AddPropertyPage = lazy(() => import("./pages/AddPropertyPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const SubscribePage = lazy(() => import("./pages/SubscribePage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const AIAssistantPage = lazy(() => import("./pages/AIAssistantPage"));
const SellerDashboard = lazy(() => import("./pages/SellerDashboard"));
const BiddingCommandCenterPage = lazy(() => import("./pages/BiddingCommandCenterPage"));
const ClaimListingPage = lazy(() => import("./pages/ClaimListingPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const PrivacySettingsPage = lazy(() => import("./pages/PrivacySettingsPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const RefundPolicyPage = lazy(() => import("./pages/RefundPolicyPage"));
const PropertyTypeLandingPage = lazy(() => import("./pages/PropertyTypeLandingPage"));
const AgencyProfilePage = lazy(() => import("./pages/AgencyProfilePage"));
const AgentProfilePage = lazy(() => import("./pages/AgentProfilePage"));
const AgencyDashboardPage = lazy(() => import("./pages/AgencyDashboardPage"));
const AgencyInviteAcceptPage = lazy(() => import("./pages/AgencyInviteAcceptPage"));
const CookiesPage = lazy(() => import("./pages/CookiesPage"));
const ComparePage = lazy(() => import("./pages/ComparePage"));
const ShortlistSharedPage = lazy(() => import("./pages/ShortlistSharedPage"));
const DaftImportPage = lazy(() => import("./pages/DaftImportPage"));
const ImportSavedSearchesPage = lazy(() => import("./pages/ImportSavedSearchesPage"));
const UkPostcodeLandingPage = lazy(() => import("./pages/UkPostcodeLandingPage"));
const UsZipLandingPage = lazy(() => import("./pages/UsZipLandingPage"));
const SavedComparisonsPage = lazy(() => import("./pages/SavedComparisonsPage"));
const UkLandingPage = lazy(() => import("./pages/UkLandingPage"));
const WhatsAppPreferencesPage = lazy(() => import("./pages/WhatsAppPreferencesPage"));
const PassportPage = lazy(() => import("./pages/PassportPage"));
const MapSearchPage = lazy(() => import("./pages/MapSearchPage"));
const CompareNeighbourhoodsPage = lazy(() => import("./pages/CompareNeighbourhoodsPage"));
const SavedSearchesPage = lazy(() => import("./pages/SavedSearchesPage"));
const AreaLandingPage = lazy(() => import("./pages/AreaLandingPage"));
const AdvertisePage = lazy(() => import("./pages/AdvertisePage"));
const ForAgentsPage = lazy(() => import("./pages/ForAgentsPage"));
const AgentBrochurePage = lazy(() => import("./pages/AgentBrochurePage"));
const DemoClaimPage = lazy(() => import("./pages/DemoClaimPage"));
const ForgotPasswordPage = lazy(() => import("./pages/AuthHardeningPages").then(m => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import("./pages/AuthHardeningPages").then(m => ({ default: m.ResetPasswordPage })));
const VerifyEmailPage = lazy(() => import("./pages/AuthHardeningPages").then(m => ({ default: m.VerifyEmailPage })));
const WasntMePage = lazy(() => import("./pages/AuthHardeningPages").then(m => ({ default: m.WasntMePage })));
const AccountSecurityPage = lazy(() => import("./pages/AccountSecurityPage"));
const TrustPage = lazy(() => import("./pages/TrustPage"));
const AdvertisePlansPage = lazy(() => import("./pages/AdvertisePlansPage"));
const InboxPage = lazy(() => import("./pages/InboxPage"));
const ChatThreadPage = lazy(() => import("./pages/ChatThreadPage"));
const AreaSponsorIndexPage = lazy(() => import("./pages/AreaSponsorIndexPage"));
const AreaSponsorBuyPage = lazy(() => import("./pages/AreaSponsorBuyPage"));
const AdvertiseSuccessPage = lazy(() => import("./pages/AdvertiseSuccessPage"));
const SoldPricesPage = lazy(() => import("./pages/SoldPricesPage"));
const SoldPricesPageUnified = lazy(() => import("./pages/SoldPricesPageUnified"));
const RealCostCalculatorPage = lazy(() => import("./pages/RealCostCalculatorPage"));
const HomeownerHubPage = lazy(() => import("./pages/HomeownerHubPage"));
const AgentMatchLandingPage = lazy(() => import("./pages/AgentMatchLandingPage"));
const AgentMatchSellPage = lazy(() => import("./pages/AgentMatchSellPage"));
const AgentMatchThanksPage = lazy(() => import("./pages/AgentMatchThanksPage"));
const AgentMatchForSellersPage = lazy(() => import("./pages/AgentMatchForSellersPage"));
const AgentMatchForAgentsPage = lazy(() => import("./pages/AgentMatchForAgentsPage"));
const AgentMatchPricingPage = lazy(() => import("./pages/AgentMatchPricingPage"));
const AgentMatchHowItWorksPage = lazy(() => import("./pages/AgentMatchHowItWorksPage"));
const AgentMatchPrivacyPage = lazy(() => import("./pages/AgentMatchPrivacyPage"));
const HomeownerBidsPage = lazy(() => import("./pages/HomeownerBidsPage"));
const AgentBiddingDashboard = lazy(() => import("./pages/AgentBiddingDashboard"));
const AgentMatchOnboardingPage = lazy(() => import("./pages/AgentMatchOnboardingPage"));
const EircodePage = lazy(() => import("./pages/EircodePage"));
const EircodeIndexPage = lazy(() => import("./pages/EircodeIndexPage"));
const PriceIndexPage = lazy(() => import("./pages/PriceIndexPage"));
const WelcomeAgentPage = lazy(() => import("./pages/WelcomeAgentPage"));
const DiscountAppliedPage = lazy(() => import("./pages/DiscountAppliedPage"));
const PriceWatchCancelPage = lazy(() => import("./pages/PriceWatchCancelPage"));
const RentalYieldsPage = lazy(() => import("./pages/RentalYieldsPage"));
const MarketIndexPage = lazy(() => import("./pages/MarketIndexPage"));
const HomeCrushPage = lazy(() => import("./pages/HomeCrushPage"));
const StyleQuizPage = lazy(() => import("./pages/StyleQuizPage"));
const HowWeVetListingsPage = lazy(() => import("./pages/HowWeVetListingsPage"));
const ListWithUsPage = lazy(() => import("./pages/ListWithUsPage"));
const SellMyHomePage = lazy(() => import("./pages/SellMyHomePage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const FaqPage = lazy(() => import("./pages/FaqPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const PartnersPage = lazy(() => import("./pages/PartnersPage"));
const PartnerApplyPage = lazy(() => import("./pages/PartnerApplyPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const BillingPortalPage = lazy(() => import("./pages/BillingPortalPage"));
const InvoicesPage = lazy(() => import("./pages/InvoicesPage"));
const AreaSeoLandingPage = lazy(() => import("./pages/AreaSeoLandingPage"));
const DesignMyRoomPage = lazy(() => import("./pages/DesignMyRoomPage"));
const DesignRoomSharePage = lazy(() => import("./pages/DesignRoomSharePage"));
const RoastPage = lazy(() => import("./pages/RoastPage"));

const RouteFallback = () => (
  <div
    data-testid="route-fallback"
    className="min-h-[60vh] flex items-center justify-center"
  >
    <div className="flex items-center gap-3 text-gray-500">
      <div className="h-5 w-5 rounded-full border-2 border-[#7042cc] border-t-transparent animate-spin" />
      <span className="text-sm">Loading…</span>
    </div>
  </div>
);

function App() {
  useEffect(() => { initAnalytics(); }, []);
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <ScrollToTop />
          <AuthProvider>
            <ShortlistImporter />
            <CompareProvider>
              <MaintenanceBanner />
              <DemoBanner />
              <Navbar />
              <AnalyticsBeacon />
              <Suspense fallback={<RouteFallback />}>
                <RouteErrorBoundary>
                <Routes>
                  <Route path="/" element={<HomePageProfessional />} />
                  <Route path="/for-agents" element={<ForAgentsPage />} />
                  <Route path="/uk/for-agents" element={<ForAgentsPage countryOverride="United Kingdom" />} />
                  <Route path="/us/for-agents" element={<ForAgentsPage countryOverride="United States" />} />
                  <Route path="/for-agents/brochure" element={<AgentBrochurePage />} />
                  <Route path="/uk/for-agents/brochure" element={<AgentBrochurePage countryOverride="United Kingdom" />} />
                  <Route path="/us/for-agents/brochure" element={<AgentBrochurePage countryOverride="United States" />} />
                  <Route path="/agents" element={<ForAgentsPage />} />
                  <Route path="/demo/claim" element={<DemoClaimPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/verify-email" element={<VerifyEmailPage />} />
                  <Route path="/security/wasnt-me" element={<WasntMePage />} />
                  <Route path="/account/security" element={<AccountSecurityPage />} />
                  <Route path="/trust" element={<TrustPage />} />
                  <Route path="/uk/trust" element={<TrustPage countryOverride="United Kingdom" />} />
                  <Route path="/us/trust" element={<TrustPage countryOverride="United States" />} />
                  <Route path="/home-crush" element={<HomeCrushPage />} />
                  <Route path="/uk/home-crush" element={<HomeCrushPage />} />
                  <Route path="/style-quiz" element={<StyleQuizPage />} />
                  <Route path="/uk/style-quiz" element={<StyleQuizPage />} />
                  <Route path="/how-we-vet-listings" element={<HowWeVetListingsPage />} />
                  <Route path="/uk/how-we-vet-listings" element={<HowWeVetListingsPage />} />
                  <Route path="/design-my-room" element={<DesignMyRoomPage />} />
                  <Route path="/uk/design-my-room" element={<DesignMyRoomPage countryOverride="United Kingdom" />} />
                  <Route path="/design-my-room/r/:renderId" element={<DesignRoomSharePage />} />
                  <Route path="/roast" element={<RoastPage />} />
                  <Route path="/listings" element={<ListingsPage />} />
                  <Route path="/uk/listings" element={<ListingsPage countryOverride="United Kingdom" />} />
                  <Route path="/us/listings" element={<ListingsPage countryOverride="United States" />} />
                  <Route path="/type/:typeSlug" element={<PropertyTypeLandingPage />} />
                  <Route path="/type/:typeSlug/:county" element={<PropertyTypeLandingPage />} />
                  <Route path="/agency/invite/:token" element={<AgencyInviteAcceptPage />} />
                  <Route path="/agency/:slug" element={<AgencyProfilePage />} />
                  <Route path="/agency/:slug/:agentSlug" element={<AgentProfilePage />} />
                  <Route path="/dashboard/agency" element={<AgencyDashboardPage />} />
                  <Route path="/buy/:county/:area" element={<AreaLandingPage listingType="sale" />} />
                  <Route path="/buy/:county" element={<AreaLandingPage listingType="sale" />} />
                  <Route path="/areas/:county/:area" element={<AreaSeoLandingPage />} />
                  {/* UK county/area landing pages — same component, same UI,
                      only the injected market profile changes so a UK visitor
                      sees £ / stamp duty land tax / UK postcodes. Strict
                      parity: no separate layout, only market-flavoured data. */}
                  <Route path="/uk/buy/:county/:area" element={<AreaLandingPage listingType="sale" countryOverride="United Kingdom" />} />
                  <Route path="/uk/buy/:county" element={<AreaLandingPage listingType="sale" countryOverride="United Kingdom" />} />
                  <Route path="/uk/rent/:county/:area" element={<AreaLandingPage listingType="rent" countryOverride="United Kingdom" />} />
                  <Route path="/uk/rent/:county" element={<AreaLandingPage listingType="rent" countryOverride="United Kingdom" />} />
                  {/* US county/area landing twins — strict parity with IE/UK */}
                  <Route path="/us/buy/:county/:area" element={<AreaLandingPage listingType="sale" countryOverride="United States" />} />
                  <Route path="/us/buy/:county" element={<AreaLandingPage listingType="sale" countryOverride="United States" />} />
                  <Route path="/us/rent/:county/:area" element={<AreaLandingPage listingType="rent" countryOverride="United States" />} />
                  <Route path="/us/rent/:county" element={<AreaLandingPage listingType="rent" countryOverride="United States" />} />
                  {/* Sold-price twins for UK (unchanged — these are a
                      different concept, not a mirror of the buy landing) */}
                  <Route path="/uk/areas/:area" element={<SoldPricesPageUnified />} />
                  <Route path="/uk/areas/:area/:sub" element={<SoldPricesPageUnified />} />
                  <Route path="/us/areas/:area" element={<SoldPricesPageUnified />} />
                  <Route path="/us/areas/:area/:sub" element={<SoldPricesPageUnified />} />
                  <Route path="/rent/:county/:area" element={<AreaLandingPage listingType="rent" />} />
                  <Route path="/rent/:county" element={<AreaLandingPage listingType="rent" />} />
                  <Route path="/buy" element={<ListingsPage />} />
                  <Route path="/rent" element={<ListingsPage />} />
                  <Route path="/uk/buy" element={<ListingsPage countryOverride="United Kingdom" />} />
                  <Route path="/uk/rent" element={<ListingsPage countryOverride="United Kingdom" />} />
                  <Route path="/us/buy" element={<ListingsPage countryOverride="United States" />} />
                  <Route path="/us/rent" element={<ListingsPage countryOverride="United States" />} />
                  <Route path="/share" element={<ListingsPage />} />
                  <Route path="/uk/share" element={<ListingsPage countryOverride="United Kingdom" />} />
                  <Route path="/us/share" element={<ListingsPage countryOverride="United States" />} />
                  <Route path="/sold" element={<ListingsPage />} />
                  <Route path="/real-cost" element={<RealCostCalculatorPage />} />
                  <Route path="/real-cost/c/:shareId" element={<RealCostCalculatorPage />} />
                  <Route path="/uk/real-cost" element={<RealCostCalculatorPage countryOverride="United Kingdom" />} />
                  <Route path="/uk/real-cost/c/:shareId" element={<RealCostCalculatorPage countryOverride="United Kingdom" />} />
                  <Route path="/us/real-cost" element={<RealCostCalculatorPage countryOverride="United States" />} />
                  <Route path="/us/real-cost/c/:shareId" element={<RealCostCalculatorPage countryOverride="United States" />} />
                  <Route path="/homeowner" element={<HomeownerHubPage />} />
                  <Route path="/uk/homeowner" element={<HomeownerHubPage countryOverride="United Kingdom" />} />
                  <Route path="/us/homeowner" element={<HomeownerHubPage countryOverride="United States" />} />
                  {/* Agent Match — two-sided marketplace, market-isolated */}
                  <Route path="/agent-match" element={<AgentMatchLandingPage />} />
                  <Route path="/uk/agent-match" element={<AgentMatchLandingPage countryOverride="United Kingdom" />} />
                  <Route path="/us/agent-match" element={<AgentMatchLandingPage countryOverride="United States" />} />
                  <Route path="/agent-match/sell" element={<AgentMatchSellPage />} />
                  <Route path="/uk/agent-match/sell" element={<AgentMatchSellPage countryOverride="United Kingdom" />} />
                  <Route path="/us/agent-match/sell" element={<AgentMatchSellPage countryOverride="United States" />} />
                  <Route path="/agent-match/thanks/:briefId" element={<AgentMatchThanksPage />} />
                  <Route path="/uk/agent-match/thanks/:briefId" element={<AgentMatchThanksPage countryOverride="United Kingdom" />} />
                  <Route path="/us/agent-match/thanks/:briefId" element={<AgentMatchThanksPage countryOverride="United States" />} />
                  <Route path="/agent-match/for-sellers" element={<AgentMatchForSellersPage />} />
                  <Route path="/uk/agent-match/for-sellers" element={<AgentMatchForSellersPage countryOverride="United Kingdom" />} />
                  <Route path="/us/agent-match/for-sellers" element={<AgentMatchForSellersPage countryOverride="United States" />} />
                  <Route path="/agent-match/for-agents" element={<AgentMatchForAgentsPage />} />
                  <Route path="/uk/agent-match/for-agents" element={<AgentMatchForAgentsPage countryOverride="United Kingdom" />} />
                  <Route path="/us/agent-match/for-agents" element={<AgentMatchForAgentsPage countryOverride="United States" />} />
                  <Route path="/agent-match/pricing" element={<AgentMatchPricingPage />} />
                  <Route path="/uk/agent-match/pricing" element={<AgentMatchPricingPage countryOverride="United Kingdom" />} />
                  <Route path="/us/agent-match/pricing" element={<AgentMatchPricingPage countryOverride="United States" />} />
                  <Route path="/agent-match/how-it-works" element={<AgentMatchHowItWorksPage />} />
                  <Route path="/uk/agent-match/how-it-works" element={<AgentMatchHowItWorksPage countryOverride="United Kingdom" />} />
                  <Route path="/us/agent-match/how-it-works" element={<AgentMatchHowItWorksPage countryOverride="United States" />} />
                  <Route path="/agent-match/privacy" element={<AgentMatchPrivacyPage />} />
                  <Route path="/uk/agent-match/privacy" element={<AgentMatchPrivacyPage countryOverride="United Kingdom" />} />
                  <Route path="/us/agent-match/privacy" element={<AgentMatchPrivacyPage countryOverride="United States" />} />
                  <Route path="/agent-match/brief/:briefId" element={<HomeownerBidsPage />} />
                  <Route path="/uk/agent-match/brief/:briefId" element={<HomeownerBidsPage countryOverride="United Kingdom" />} />
                  <Route path="/us/agent-match/brief/:briefId" element={<HomeownerBidsPage countryOverride="United States" />} />
                  <Route path="/agent-match/agent" element={<AgentBiddingDashboard />} />
                  <Route path="/uk/agent-match/agent" element={<AgentBiddingDashboard countryOverride="United Kingdom" />} />
                  <Route path="/us/agent-match/agent" element={<AgentBiddingDashboard countryOverride="United States" />} />
                  <Route path="/agent-match/agent/onboarding" element={<AgentMatchOnboardingPage />} />
                  <Route path="/uk/agent-match/agent/onboarding" element={<AgentMatchOnboardingPage countryOverride="United Kingdom" />} />
                  <Route path="/us/agent-match/agent/onboarding" element={<AgentMatchOnboardingPage countryOverride="United States" />} />
                  <Route path="/area-prices" element={<EircodeIndexPage />} />
                  <Route path="/area-prices/:routingKey" element={<EircodePage />} />
                  {/* UK URL aliases — the unified SoldPricesPage serves both
                      the browse-index (no area) and per-outward deep dive,
                      mirroring /area-prices and /area-prices/:routingKey */}
                  <Route path="/uk/area-prices" element={<SoldPricesPageUnified />} />
                  <Route path="/uk/area-prices/:area" element={<SoldPricesPageUnified />} />
                  <Route path="/us/area-prices" element={<SoldPricesPageUnified />} />
                  <Route path="/us/area-prices/:area" element={<SoldPricesPageUnified />} />
                  <Route path="/price-index" element={<PriceIndexPage />} />
                  <Route path="/price-index/:countySlug" element={<PriceIndexPage />} />
                  <Route path="/welcome-agent" element={<WelcomeAgentPage />} />
                  <Route path="/discount-applied" element={<DiscountAppliedPage />} />
                  <Route path="/price-watch/cancel" element={<PriceWatchCancelPage />} />
                  <Route path="/new-homes" element={<ListingsPage />} />
                  <Route path="/uk/new-homes" element={<ListingsPage countryOverride="United Kingdom" />} />
                  <Route path="/property/:id" element={<PropertyDetailPage />} />
                  <Route path="/uk/property/:id" element={<PropertyDetailPage countryOverride="United Kingdom" />} />
                  <Route path="/us/property/:id" element={<PropertyDetailPage countryOverride="United States" />} />
                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route path="/saved" element={<DashboardPage />} />
                  <Route path="/inbox" element={<InboxPage />} />
                  <Route path="/inbox/:chatId" element={<ChatThreadPage />} />
                  <Route path="/add-property" element={<AddPropertyPage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/uk/login" element={<LoginPage countryOverride="United Kingdom" />} />
                  <Route path="/us/login" element={<LoginPage countryOverride="United States" />} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route path="/uk/register" element={<RegisterPage countryOverride="United Kingdom" />} />
                  <Route path="/us/register" element={<RegisterPage countryOverride="United States" />} />
                  {/* Legacy / common-typo redirects (some users + Google followed dead links here). */}
                  <Route path="/signup" element={<Navigate to="/register" replace />} />
                  <Route path="/sign-up" element={<Navigate to="/register" replace />} />
                  <Route path="/signin" element={<Navigate to="/login" replace />} />
                  <Route path="/pricing" element={<PricingPage />} />
                  <Route path="/uk/pricing" element={<PricingPage countryOverride="United Kingdom" />} />
                  <Route path="/us/pricing" element={<PricingPage countryOverride="United States" />} />
                  <Route path="/subscribe/:planId" element={<SubscribePage />} />
                  {/* Stealth admin route — moved off /admin. /admin returns a generic 404. */}
                  <Route path="/_/staff-room" element={<AdminPage />} />
                  <Route path="/admin" element={<NotFoundPage />} />
                  <Route path="/404" element={<NotFoundPage />} />
                  <Route path="/ai-assistant" element={<AIAssistantPage />} />
                  <Route path="/uk/ai-assistant" element={<AIAssistantPage countryOverride="United Kingdom" />} />
                  <Route path="/seller-dashboard" element={<SellerDashboard />} />
                  <Route path="/dashboard/bidding" element={<BiddingCommandCenterPage />} />
                  <Route path="/claim/:token" element={<ClaimListingPage />} />
                  <Route path="/dashboard/billing" element={<BillingPortalPage />} />
                  <Route path="/dashboard/billing/invoices" element={<InvoicesPage />} />
                  <Route path="/privacy" element={<PrivacyPage />} />
                  <Route path="/uk/privacy" element={<PrivacyPage countryOverride="United Kingdom" />} />
                  <Route path="/us/privacy" element={<PrivacyPage countryOverride="United States" />} />
                  <Route path="/account/privacy" element={<PrivacySettingsPage />} />
                  <Route path="/uk/account/privacy" element={<PrivacySettingsPage countryOverride="United Kingdom" />} />
                  <Route path="/us/account/privacy" element={<PrivacySettingsPage countryOverride="United States" />} />
                  <Route path="/terms" element={<TermsPage />} />
                  <Route path="/uk/terms" element={<TermsPage countryOverride="United Kingdom" />} />
                  <Route path="/us/terms" element={<TermsPage countryOverride="United States" />} />
                  <Route path="/refund-policy" element={<RefundPolicyPage />} />
                  <Route path="/uk/refund-policy" element={<RefundPolicyPage countryOverride="United Kingdom" />} />
                  <Route path="/us/refund-policy" element={<RefundPolicyPage countryOverride="United States" />} />
                  <Route path="/cookies" element={<CookiesPage />} />
                  <Route path="/uk/cookies" element={<CookiesPage countryOverride="United Kingdom" />} />
                  <Route path="/us/cookies" element={<CookiesPage countryOverride="United States" />} />
                  <Route path="/list-with-us" element={<ListWithUsPage />} />
                  <Route path="/uk/list-with-us" element={<ListWithUsPage countryOverride="United Kingdom" />} />
                  <Route path="/sell-my-home" element={<SellMyHomePage />} />
                  <Route path="/uk/sell-my-home" element={<SellMyHomePage countryOverride="United Kingdom" />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/faq" element={<FaqPage />} />
                  <Route path="/uk/faq" element={<FaqPage countryOverride="United Kingdom" />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/uk/about" element={<AboutPage countryOverride="United Kingdom" />} />
                  <Route path="/us/about" element={<AboutPage countryOverride="United States" />} />
                  <Route path="/partners" element={<PartnersPage />} />
                  <Route path="/partners/apply" element={<PartnerApplyPage />} />
                  <Route path="/compare" element={<ComparePage />} />
                  <Route path="/uk/compare" element={<ComparePage countryOverride="United Kingdom" />} />
                  <Route path="/us/compare" element={<ComparePage countryOverride="United States" />} />
                  <Route path="/s/:slug" element={<ShortlistSharedPage />} />
                  <Route path="/migrate-from-daft" element={<DaftImportPage />} />
                  <Route path="/import-saved-searches" element={<ImportSavedSearchesPage />} />
                  <Route path="/uk" element={<HomePageProfessional countryOverride="United Kingdom" />} />
                  <Route path="/us" element={<HomePageProfessional countryOverride="United States" />} />
                  <Route path="/uk-old" element={<UkLandingPage />} />
                  <Route path="/uk/postcode/:outward" element={<UkPostcodeLandingPage />} />
                  <Route path="/us/zip/:zip" element={<UsZipLandingPage />} />
                  <Route path="/my-comparisons" element={<SavedComparisonsPage />} />
                  <Route path="/uk/my-comparisons" element={<SavedComparisonsPage countryOverride="United Kingdom" />} />
                  <Route path="/us/my-comparisons" element={<SavedComparisonsPage countryOverride="United States" />} />
                  <Route path="/account/whatsapp" element={<WhatsAppPreferencesPage />} />
                  <Route path="/passport/:propertyId" element={<PassportPage />} />
                  <Route path="/map" element={<MapSearchPage />} />
                  <Route path="/compare-neighbourhoods" element={<CompareNeighbourhoodsPage />} />
                  <Route path="/uk/compare-neighbourhoods" element={<CompareNeighbourhoodsPage countryOverride="United Kingdom" />} />
                  <Route path="/us/compare-neighbourhoods" element={<CompareNeighbourhoodsPage countryOverride="United States" />} />
                  <Route path="/uk/map" element={<MapSearchPage countryOverride="United Kingdom" />} />
                  <Route path="/us/map" element={<MapSearchPage countryOverride="United States" />} />
                  <Route path="/saved-searches" element={<SavedSearchesPage />} />
                  <Route path="/advertise" element={<AdvertisePage />} />
                  <Route path="/advertise/plans" element={<AdvertisePlansPage />} />
                  <Route path="/uk/advertise/plans" element={<AdvertisePlansPage countryOverride="United Kingdom" />} />
                  <Route path="/us/advertise/plans" element={<AdvertisePlansPage countryOverride="United States" />} />
                  <Route path="/advertise/plans/:tier" element={<AdvertisePlansPage />} />
                  <Route path="/uk/advertise/plans/:tier" element={<AdvertisePlansPage countryOverride="United Kingdom" />} />
                  <Route path="/us/advertise/plans/:tier" element={<AdvertisePlansPage countryOverride="United States" />} />
                  <Route path="/advertise/area-sponsor" element={<AreaSponsorIndexPage />} />
                  <Route path="/advertise/area-sponsor/:county/:area" element={<AreaSponsorBuyPage />} />
                  <Route path="/advertise/area-sponsor/:county" element={<AreaSponsorBuyPage />} />
                  {/* UK twins — same components; market-aware via useMarket */}
                  <Route path="/uk/advertise" element={<AdvertisePage />} />
                  <Route path="/uk/advertise/area-sponsor" element={<AreaSponsorIndexPage />} />
                  <Route path="/uk/advertise/area-sponsor/:county/:area" element={<AreaSponsorBuyPage />} />
                  <Route path="/uk/advertise/area-sponsor/:county" element={<AreaSponsorBuyPage />} />
                  <Route path="/us/advertise" element={<AdvertisePage />} />
                  <Route path="/us/advertise/area-sponsor" element={<AreaSponsorIndexPage />} />
                  <Route path="/us/advertise/area-sponsor/:county/:area" element={<AreaSponsorBuyPage />} />
                  <Route path="/us/advertise/area-sponsor/:county" element={<AreaSponsorBuyPage />} />
                  <Route path="/advertise/success" element={<AdvertiseSuccessPage />} />
                  <Route path="/sold/:county/:area" element={<SoldPricesPage />} />
                  <Route path="/sold/:county" element={<SoldPricesPage />} />
                  {/* Phase 4 — unified market-aware route. Single component
                      drives UK, IE (when ?country=IE&area=ROUTING_KEY), and
                      USA (when wired). The legacy /uk/sold/* paths now point
                      here too — one file, one source of truth. */}
                  <Route path="/area-sold/:area" element={<SoldPricesPageUnified />} />
                  <Route path="/area-sold" element={<SoldPricesPageUnified />} />
                  <Route path="/uk/sold" element={<SoldPricesPageUnified />} />
                  <Route path="/uk/sold/:area" element={<SoldPricesPageUnified />} />
                  <Route path="/us/sold" element={<SoldPricesPageUnified />} />
                  <Route path="/us/sold/:area" element={<SoldPricesPageUnified />} />
                  <Route path="/sold-prices" element={<MarketIndexPage kind="sold" />} />
                  <Route path="/yields/:county/:area" element={<RentalYieldsPage />} />
                  <Route path="/yields/:county" element={<RentalYieldsPage />} />
                  <Route path="/yields" element={<MarketIndexPage kind="yields" />} />
                  {/* UK + USA twins — same component, market-aware "coming
                      soon" empty state until rental data is ingested. */}
                  <Route path="/uk/yields" element={<RentalYieldsPage />} />
                  <Route path="/uk/yields/:area" element={<RentalYieldsPage />} />
                  <Route path="/us/yields" element={<RentalYieldsPage />} />
                  <Route path="/us/yields/:area" element={<RentalYieldsPage />} />
                  {/* Catch-all — must be last */}
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
                </RouteErrorBoundary>
              </Suspense>
              <ChromeShell />
            </CompareProvider>
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

/**
 * Extra UI that sits below every route (Footer, floating trays, cookie
 * banner, install prompt, toaster).
 *
 * The map page uses a viewport-pinned layout for its map+list section
 * (`h-[calc(100vh-80px)] overflow-hidden`), but users still expect the
 * site footer at the bottom of the page — so it's rendered here on all
 * routes including `/map`. The map section has its own `overflow-hidden`
 * so document scroll doesn't drag the map pins around; users scroll
 * down past the map to reach the footer.
 *
 * `CompareTray` stays hidden on `/map*` because it's a floating bar
 * that would sit on top of the map controls.
 */
const ChromeShell = () => {
  const { pathname } = useLocation();
  const isMap = /^\/(uk\/|us\/)?map(\/|$)/.test(pathname);
  return (
    <>
      <Footer />
      {!isMap && <CompareTray />}
      <CookieConsent />
      <InstallPrompt />
      <Toaster />
    </>
  );
};

export default App;
